import { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip, Divider, useTheme,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  previewProjectDeployment,
  launchProjectDeployment,
} from "../utils/api";

/**
 * Deployment chat dialog. Single round trip:
 *   1. Composer authors + validates spec (preview)
 *   2. Shows the narrated plan
 *   3. User confirms → launch (provisions GCP)
 *   4. Closes; Live tab takes over.
 *
 * Per project_deployment_automation.md — "narrate defaults, confirm don't ask."
 * The dialog never asks free-form questions; it shows what the composer
 * inferred and lets the user redirect (e.g., change cadence) post-deploy.
 */
export default function DeploymentDialog({
  open, projectId, projectName, onClose, onDeployed,
}) {
  const theme = useTheme();
  const [phase, setPhase] = useState("idle"); // idle | previewing | preview-ready | launching | launched | error
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  // Kick off preview on open
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setPhase("previewing");
    setError("");
    setPreview(null);
    previewProjectDeployment(projectId)
      .then((res) => {
        if (cancelled) return;
        setPreview(res);
        setPhase("preview-ready");
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.error || e?.message || "Preview failed";
        setError(msg);
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const handleLaunch = async () => {
    setPhase("launching");
    setError("");
    try {
      // Real Cloud Scheduler provisioning is live (see deploy_to_cloud.py).
      // Contract defended by dco_001 (create/verify/teardown) and dco_002
      // (scheduler fires → refresh works); both green against dev backend.
      const res = await launchProjectDeployment(projectId, { dry_run: false });
      setPhase("launched");
      if (onDeployed) onDeployed(res);
      // Auto-close after a short pause so the user sees the success message
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Launch failed");
      setPhase("error");
    }
  };

  const ready = phase === "preview-ready" && preview?.ready_to_launch;
  const validationIssues = !ready && phase === "preview-ready";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <RocketLaunchIcon sx={{ color: theme.palette.primary.main }} />
        Deploy <Box component="span" sx={{ fontWeight: 700 }}>{projectName || "this project"}</Box>
      </DialogTitle>
      <DialogContent>
        {phase === "previewing" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Analyzing your project and composing the deployment plan…
            </Typography>
          </Box>
        )}

        {phase === "preview-ready" && preview && (
          <Box>
            <PlanNarration text={preview.narration} />
            {validationIssues && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Validation didn't pass. See above. The composer can retry by
                regenerating the composition.
              </Alert>
            )}
            <SectionIntents spec={preview.spec} />
            <PlanFacts spec={preview.spec} />
          </Box>
        )}

        {phase === "launching" && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Provisioning the schedule and storage…
            </Typography>
          </Box>
        )}

        {phase === "launched" && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Deployed. Your Live tab will refresh on the schedule.
          </Alert>
        )}

        {phase === "error" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || "Something went wrong."}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={phase === "launching"}>
          {phase === "launched" ? "Close" : "Cancel"}
        </Button>
        {phase === "preview-ready" && (
          <Button
            variant="contained"
            disabled={!ready}
            onClick={handleLaunch}
          >
            {ready ? "Confirm and deploy" : "Cannot deploy (see issues)"}
          </Button>
        )}
        {phase === "error" && (
          <Button
            variant="contained"
            onClick={() => {
              setPhase("previewing");
              setError("");
              previewProjectDeployment(projectId)
                .then((res) => { setPreview(res); setPhase("preview-ready"); })
                .catch((e) => {
                  setError(e?.response?.data?.error || e?.message || "Preview failed");
                  setPhase("error");
                });
            }}
          >
            Retry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PlanNarration({ text }) {
  return (
    <Box
      sx={{
        whiteSpace: "pre-wrap",
        fontSize: 14,
        lineHeight: 1.55,
        color: "text.primary",
        bgcolor: "action.hover",
        p: 1.5,
        borderRadius: 1.5,
      }}
    >
      {text || ""}
    </Box>
  );
}

// Map internal shape ids to user-facing labels (no underscores, no internals).
const SHAPE_LABELS = {
  ml_binary_scoring: "predictive model",
  ml_regression_scoring: "predictive model",
  sql_dashboard: "dashboard",
  file_upload_scoring: "upload-scored model",
  periodic_html_report: "recurring report",
  statistical_estimation: "statistical estimate",
  recommendation_feed: "recommendation feed",
};

// Strip project/dataset prefix — show just the table name. Implementation
// detail (BQ project/dataset) doesn't belong on the user surface.
function shortTable(fq) {
  if (!fq) return null;
  const parts = String(fq).split(".");
  return parts[parts.length - 1] || fq;
}

// Map per-section intent → {label, color, hint} for the badge UI.
// These mirror the values understand_deployment_goal emits.
const INTENT_LABEL = {
  keep: { label: "live refresh", color: "success", hint: "Same code, fresh data each refresh." },
  transform_score: { label: "scored live", color: "primary", hint: "Training swapped to scoring against the saved model." },
  drop: { label: "build only", color: "default", hint: "One-time analysis — not re-run on the live page." },
  augment: { label: "new live section", color: "info", hint: "Added for the live page; not in the build report." },
};

function SectionIntents({ spec }) {
  const intents = spec?.validation?.intents || {};
  const sections = spec?.sections_for_live || [];
  if (!intents || Object.keys(intents).length === 0) return null;

  // Build the rendered list: every section the planner classified.
  // Prefer the spec's section list (with titles) when available, else fall
  // back to bare ids from the intents map.
  const sectionById = Object.fromEntries(sections.map((s) => [s.id, s]));
  const rows = Object.entries(intents).map(([sid, intent]) => ({
    id: sid,
    title: sectionById[sid]?.title || sid,
    intent,
    label: INTENT_LABEL[intent] || { label: intent, color: "default" },
  }));

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
        What carries forward to live
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {rows.map((r) => (
          <Box
            key={r.id}
            sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12 }}
          >
            <Chip
              label={r.label.label}
              size="small"
              color={r.label.color}
              variant={r.intent === "drop" ? "outlined" : "filled"}
              sx={{ fontSize: 10, height: 18, minWidth: 110 }}
            />
            <Box sx={{ color: "text.primary" }}>{r.title}</Box>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
              {r.label.hint}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function PlanFacts({ spec }) {
  if (!spec) return null;
  const chips = [];
  if (spec.shape) chips.push({ label: SHAPE_LABELS[spec.shape] || "project", color: "primary" });
  if (spec.trigger?.type) {
    chips.push({
      label: spec.trigger.type.replace(/^trigger_/, "").replace(/_/g, " "),
    });
  }
  const shortName = shortTable(spec.input?.table);
  if (shortName) chips.push({ label: shortName });
  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        Plan details
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {chips.map((c, i) => (
          <Chip
            key={i}
            label={c.label}
            size="small"
            color={c.color || "default"}
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
        ))}
      </Box>
    </Box>
  );
}
